import type React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import posthog from "posthog-js";

export interface AnalyticsEventProperties {
  [key: string]: string | number | boolean | null | undefined;
}

export interface AnalyticsUserProperties {
  [key: string]: string | number | boolean | null | undefined;
}

export interface AnalyticsContextType {
  trackEvent: (
    eventName: string,
    properties?: AnalyticsEventProperties,
  ) => void;
  identify: (userId: string, userProperties?: AnalyticsUserProperties) => void;
  reset: () => void;
}

export const AnalyticsContext = createContext<AnalyticsContextType | null>(
  null,
);

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error("useAnalytics must be used within an AnalyticsProvider");
  }
  return context;
};

interface AnalyticsProviderProps {
  children: React.ReactNode;
  posthogKey: string | null;
  posthogEnabled: string | null;
}

function usePosthogInit(
  posthogKey: string | null,
  posthogEnabled: string | null,
) {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!posthogKey || isInitialized) return;

    if (posthogEnabled === "true") {
      console.log("Analytics: Posthog is enabled");
      posthog.init(posthogKey, {
        api_host: "https://eu.i.posthog.com",
        person_profiles: "identified_only",
      });
      setIsInitialized(true);
    } else {
      console.log("Analytics: Posthog is disabled");
    }
  }, [posthogKey, posthogEnabled, isInitialized]);

  return isInitialized;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
  children,
  posthogKey,
  posthogEnabled,
}) => {
  console.log("AnalyticsProvider", posthogKey, posthogEnabled);
  usePosthogInit(posthogKey, posthogEnabled);

  const trackEvent = useCallback(
    (eventName: string, properties?: AnalyticsEventProperties) => {
      if (posthogEnabled === "true") {
        posthog.capture(eventName, properties);
      } else {
        console.log(`Analytics event: ${eventName}`, properties);
      }
    },
    [posthogEnabled],
  );

  const identify = useCallback(
    (userId: string, userProperties?: AnalyticsUserProperties) => {
      if (posthogEnabled === "true") {
        posthog.identify(userId, userProperties);
      } else {
        console.log(`Analytics identify: ${userId}`, userProperties);
      }
    },
    [posthogEnabled],
  );

  const reset = useCallback(() => {
    if (posthogEnabled === "true") {
      posthog.reset();
    } else {
      console.log("Analytics reset");
    }
  }, [posthogEnabled]);

  const value = useMemo(() => {
    return { trackEvent, identify, reset };
  }, [trackEvent, identify, reset]);

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};
